import {
  handleRemoveFcmTokenService,
  handleUpdateFcmTokenService,
} from '../services/users';

// Not cleared by logoutHelper: they describe this browser, not the session
const DEVICE_ID_KEY = 'webDeviceId';
const LAST_FCM_TOKEN_KEY = 'lastFcmToken';
const LAST_FCM_SYNC_KEY = 'lastFcmTokenSyncAt';

// Re-register at least daily so the backend doesn't treat this browser as stale
const FCM_RESYNC_INTERVAL_MS = 24 * 60 * 60 * 1000;

const generateDeviceId = () =>
  typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
    ? crypto.randomUUID()
    : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;

export const getWebDeviceId = () => {
  let deviceId = localStorage.getItem(DEVICE_ID_KEY);
  if (!deviceId) {
    deviceId = generateDeviceId();
    localStorage.setItem(DEVICE_ID_KEY, deviceId);
  }
  return deviceId;
};

export const getFcmDeviceFields = (fcmToken: string) => ({
  fcmToken,
  platform: 'web' as const,
  deviceId: getWebDeviceId(),
});

export const markFcmTokenRegistered = (fcmToken: string) => {
  localStorage.setItem(LAST_FCM_TOKEN_KEY, fcmToken);
  localStorage.setItem(LAST_FCM_SYNC_KEY, String(Date.now()));
};

/**
 * Registers this browser's token for the logged-in user when it changed
 * or wasn't sent in the last day. Never throws.
 */
export const syncFcmToken = async (fcmToken: string | null) => {
  if (!fcmToken || !localStorage.getItem('loggedIn')) return;

  const lastToken = localStorage.getItem(LAST_FCM_TOKEN_KEY);
  const lastSyncAt = Number(localStorage.getItem(LAST_FCM_SYNC_KEY)) || 0;
  if (
    fcmToken === lastToken &&
    Date.now() - lastSyncAt < FCM_RESYNC_INTERVAL_MS
  ) {
    return;
  }

  try {
    await handleUpdateFcmTokenService({
      ...getFcmDeviceFields(fcmToken),
      ...(lastToken && lastToken !== fcmToken && {replaceToken: lastToken}),
    });
    markFcmTokenRegistered(fcmToken);
  } catch (error) {
    console.warn('Failed to sync notification token:', error);
  }
};

/**
 * Stops pushes to this browser for the user logging out; other devices keep
 * receiving them. Call before the auth token is cleared. Never throws.
 */
export const unregisterFcmTokenOnLogout = async () => {
  const lastToken = localStorage.getItem(LAST_FCM_TOKEN_KEY);
  localStorage.removeItem(LAST_FCM_TOKEN_KEY);
  localStorage.removeItem(LAST_FCM_SYNC_KEY);

  if (!lastToken || !localStorage.getItem('userToken')) return;

  try {
    await handleRemoveFcmTokenService(lastToken);
  } catch (error) {
    console.warn('Failed to remove notification token:', error);
  }
};
